import { useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function MultiSelectFilter<K extends string | number>({
  label,
  options,
  value,
  onChange,
  searchable = false,
  anyLabel = 'Any',
  hideLabel = false,
  triggerClassName = 'min-w-[9rem]',
  capitalize = true,
}: {
  label: string;
  options: { key: K; label: string }[];
  value: K[];
  onChange: (next: K[]) => void;
  /** A search box at the top of the menu, for lists too long to scan. */
  searchable?: boolean;
  /** What the button says when nothing is picked (nothing picked = no filter). */
  anyLabel?: string;
  /** Drop the caption above the button when the context already names it. */
  hideLabel?: boolean;
  triggerClassName?: string;
  /** Title-case the labels (on for raw keys like role names; off for phone numbers). */
  capitalize?: boolean;
}) {
  const [query, setQuery] = useState('');
  const toggle = (key: K) =>
    onChange(value.includes(key) ? value.filter((v) => v !== key) : [...value, key]);
  const summary =
    value.length === 0
      ? anyLabel
      : value.length === 1
        ? options.find((o) => o.key === value[0])?.label ?? '1 selected'
        : `${value.length} selected`;
  const needle = query.trim().toLowerCase();
  const shown = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;

  return (
    <div className="space-y-1">
      {!hideLabel && <span className="block text-xs font-medium text-gray-500">{label}</span>}
      <DropdownMenu onOpenChange={(open) => !open && setQuery('')}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" aria-label={label} className={`justify-between gap-2 font-normal ${triggerClassName}`}>
            <span className={`truncate ${capitalize ? 'capitalize' : ''}`}>{summary}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto">
          <DropdownMenuLabel>{label}</DropdownMenuLabel>
          {searchable && (
            <div className="relative px-2 pb-1.5">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                // The menu's own typeahead would otherwise eat the keystrokes.
                onKeyDown={(e) => e.key !== 'Escape' && e.stopPropagation()}
                placeholder="Search..."
                aria-label={`Search ${label.toLowerCase()}`}
                className="w-full rounded-md border border-gray-300 py-1.5 pl-7 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <DropdownMenuSeparator />
          {shown.length === 0 && <p className="px-2 py-1.5 text-sm text-gray-500">No matches</p>}
          {shown.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.key}
              className={capitalize ? 'capitalize' : undefined}
              checked={value.includes(o.key)}
              // Keep the menu open so several values can be ticked in one go.
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => toggle(o.key)}
            >
              {o.label}
            </DropdownMenuCheckboxItem>
          ))}
          {value.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => onChange([])}>Clear</DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
